<template>
  <VueFinalModal
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    :click-to-close="true"
    :esc-to-close="true"
    @closed="handleDismiss"
  >
    <div class="layout" @click.self="handleDismiss">
      <div class="success-card">
        <div class="success-title">{{ resolvedTitle }}</div>

        <div class="success-items">
          <div v-for="item in resolvedItems" :key="item.proposalId" class="success-item">
            <div class="success-when">{{ whenLabel(item) }}</div>
            <div v-if="item.locationName" class="success-spot">
              <GamIcon class="success-spot-pin" :name="GamIconName.PIN" size="12px" is-current />
              <span class="success-spot-name">{{ item.locationName }}</span>
            </div>
            <button type="button" class="success-view" @click="selectItem(item)">
              {{ localize('spark.gambit_exists.view') }}
            </button>
          </div>
        </div>

        <div class="success-close" @click="handleDismiss">
          <GamIcon :name="GamIconName.X_CLOSE" size="24px" is-current />
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>

<script setup lang="ts">
import { localize } from '@/core/gambits';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { formatSuccessWhenLabel } from '@/views/main/sparks/utils/success-when-label';
import { computed } from 'vue';
import { VueFinalModal } from 'vue-final-modal';

export interface SuccessListDialogItem {
  proposalId: string;
  gambitId: string;
  date: string;
  timeSpanStart?: string | null;
  timeZone?: string;
  locationName?: string | null;
}

interface SuccessListDialogProps {
  title?: string;
  items?: SuccessListDialogItem[];
  getItems?: () => SuccessListDialogItem[];
  onSelect: (item: SuccessListDialogItem) => void;
}

const props = withDefaults(defineProps<SuccessListDialogProps>(), {
  title: '',
  items: () => [],
  getItems: undefined,
});

const resolvedItems = computed(() => {
  if (props.getItems) {
    return props.getItems();
  }

  return props.items || [];
});

const resolvedTitle = computed(() => props.title || localize('spark.success.title'));

let hasSelected = false;

const selectItem = (item: SuccessListDialogItem) => {
  if (hasSelected) {
    return;
  }

  hasSelected = true;
  props.onSelect(item);
};

const handleDismiss = () => {
  const first = resolvedItems.value[0];
  if (first) {
    selectItem(first);
  }
};

const whenLabel = (item: SuccessListDialogItem): string =>
  formatSuccessWhenLabel(item.date, item.timeSpanStart, item.timeZone);
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

.layout {
  width: 100svw;
  height: 100svh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.success-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: min(100%, 300px);
  max-height: min(70vh, 640px);
  margin: 2rem;
  padding: var(--spacing-xxl) var(--spacing-xl);
  padding-bottom: 6.5rem;
  background: var(--color-dark-700);
  border: 1px solid var(--color-white-10);
  border-radius: var(--radius-large);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.success-title {
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  color: var(--color-white);
  margin-bottom: var(--spacing-xl);
}

.success-items {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.success-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--spacing-s);
}

.success-when {
  font-size: 17px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--color-white);
}

.success-spot {
  display: inline-flex;
  align-items: center;
  gap: 0.3em;
  min-width: 0;
  max-width: 100%;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.033em;
  line-height: 1.35;
  color: var(--color-white-70);
}

.success-spot-pin {
  flex-shrink: 0;
}

.success-spot-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.success-view {
  width: 100%;
  margin-top: var(--spacing-s);
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 2rem;
  background: var(--color-linear-gradient);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 200ms ease;

  @include hover {
    opacity: 0.9;
  }

  @include press;
}

.success-close {
  position: absolute;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  touch-action: manipulation;
  transition: all 200ms linear;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press($transform: translateX(-50%));
}
</style>
